import { useMemo } from "react";

import { getExtensionValue } from "@medplum/core";
import { CodeableConcept, QuestionnaireItem } from "@medplum/fhirtypes";

import { Flex, Textarea, TextareaProps } from "@mantine/core";

import MaskInput, { MaskInputProps } from "@/components/MaskInput";
import { TextWrapper } from "@/components/TextWrapper";
import { Extensions } from "@/fhir/enums/extensions";
import { getCodeableConceptCode } from "@/utils/medplum";

interface QuestionnaireStringFieldProps extends MaskInputProps {
  questionnaireItem: QuestionnaireItem;
  minRange?: number;
  maxRange?: number;
  "data-testid"?: string;
}

export default function QuestionnaireStringField({
  error,
  mask,
  leftSection,
  minRange,
  maxRange,
  questionnaireItem,
  rightSection,
  "data-testid": dataTestId,
  ...props
}: QuestionnaireStringFieldProps) {
  const itemControl = getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemControl) as
    | CodeableConcept
    | undefined;

  const rightSectionWidth = useMemo(() => {
    if (!rightSection || typeof rightSection !== "string") return undefined;
    const minWidth = 70;
    const charWidth = 8;
    return Math.max(minWidth, rightSection.length * charWidth + 32);
  }, [rightSection]);

  const maxLength = questionnaireItem.maxLength;

  if (getCodeableConceptCode(itemControl) === "text-box") {
    return (
      <Textarea
        autosize
        error={error}
        maxLength={maxLength}
        minRows={minRange || 2}
        maxRows={maxRange || 6}
        data-testid={dataTestId}
        rightSection={rightSection}
        rightSectionWidth={rightSectionWidth}
        {...(props as TextareaProps)}
      />
    );
  }

  const styledRightSection =
    rightSection && typeof rightSection === "string" ? (
      <Flex
        px="8px"
        h="100%"
        w="100%"
        bg="gray.1"
        ta="center"
        align="center"
        justify="center"
        style={{
          borderLeft: "1px solid var(--mantine-color-gray-4)",
          borderRadius: "0 4px 4px 0",
        }}
      >
        <TextWrapper c="gray.7" variant="bold">
          {rightSection}
        </TextWrapper>
      </Flex>
    ) : (
      rightSection
    );

  return (
    <MaskInput
      error={error}
      mask={mask || ""}
      maxLength={maxLength}
      data-testid={dataTestId}
      leftSection={leftSection}
      rightSection={styledRightSection}
      rightSectionWidth={rightSectionWidth}
      {...props}
    />
  );
}
